/**
 * 蚂蚁线着色器（M2-07 / DESIGN.md §6.2「选区运算」的可视化）。
 *
 * 输入：选区蒙版（r8unorm 纹理，文档分辨率）+ 视口变换 + 动画相位。
 * 片元流程：屏幕像素 → 逆视口变换到文档坐标 → 判定「选中且四邻域有未选中」
 * 即边界 → 沿 (x+y+phase) 方向铺黑白相间的斜纹。相位随时间推进，
 * 斜纹沿边界爬行——这就是蚂蚁。非边界像素输出全透明，叠加在画布上方。
 *
 * 两种方言的坐标差异都在片元内消化：
 * - GLSL 的 gl_FragCoord 原点在左下，用 u_viewH 翻转到左上约定；
 * - WebGL2 的 writeTexture 按左上约定预翻转存储（见 backend），
 *   texelFetch 时用 u_docH−1−y 换算回数据行。
 *
 * uniform 全部用标量 f32：backend 的 packUniforms 按名字典序紧密打包，
 * 标量序列与 WGSL 结构体逐字段对齐是最不容易踩错的布局（vec3 被禁，
 * vec2/vec4 的 8/16 字节对齐也会与紧密打包错位——M1-11 的教训）。
 */
import type { ShaderSource } from '../../backend';

/** 蚂蚁线的 uniform。字段名的字典序决定缓冲布局，改名必须两份着色器同步。 */
export interface AntsUniforms {
  /** 斜纹半周期（屏幕像素）。 */
  readonly u_dash: number;
  readonly u_docH: number;
  readonly u_docW: number;
  /** 动画相位（屏幕像素）。每帧递增即产生爬行。 */
  readonly u_phase: number;
  /** 视口旋转（弧度）。 */
  readonly u_rot: number;
  readonly u_scale: number;
  readonly u_tx: number;
  readonly u_ty: number;
  readonly u_viewH: number;
  readonly u_viewW: number;
}

export function antsShaderSource(): ShaderSource {
  return {
    wgsl: /* wgsl */ `
struct Params {
  u_dash: f32,
  u_docH: f32,
  u_docW: f32,
  u_phase: f32,
  u_rot: f32,
  u_scale: f32,
  u_tx: f32,
  u_ty: f32,
  u_viewH: f32,
  u_viewW: f32,
};
@group(0) @binding(0) var<uniform> params: Params;
@group(0) @binding(1) var u_mask: texture_2d<f32>;

@vertex
fn vs_main(@builtin(vertex_index) idx: u32) -> @builtin(position) vec4f {
  var pos = array<vec2f, 3>(vec2f(-1.0, -1.0), vec2f(3.0, -1.0), vec2f(-1.0, 3.0));
  return vec4f(pos[idx], 0.0, 1.0);
}

fn doc_at(screen: vec2f) -> vec2f {
  let d = screen - vec2f(params.u_tx, params.u_ty);
  let c = cos(params.u_rot);
  let s = sin(params.u_rot);
  return vec2f(d.x * c + d.y * s, -d.x * s + d.y * c) / params.u_scale;
}

fn selected_at(doc: vec2f) -> bool {
  if (doc.x < 0.0 || doc.y < 0.0 || doc.x >= params.u_docW || doc.y >= params.u_docH) {
    return false;
  }
  return textureLoad(u_mask, vec2<i32>(i32(doc.x), i32(doc.y)), 0).r >= 0.5;
}

@fragment
fn fs_main(@builtin(position) pos: vec4f) -> @location(0) vec4f {
  let screen = pos.xy;
  let center = selected_at(doc_at(screen));
  if (!center) {
    return vec4f(0.0);
  }
  let boundary =
    !selected_at(doc_at(screen + vec2f(1.0, 0.0))) ||
    !selected_at(doc_at(screen - vec2f(1.0, 0.0))) ||
    !selected_at(doc_at(screen + vec2f(0.0, 1.0))) ||
    !selected_at(doc_at(screen - vec2f(0.0, 1.0)));
  if (!boundary) {
    return vec4f(0.0);
  }
  let t = floor((screen.x + screen.y + params.u_phase) / params.u_dash);
  if (t % 2.0 == 0.0) {
    return vec4f(0.0, 0.0, 0.0, 1.0);
  }
  return vec4f(1.0, 1.0, 1.0, 1.0);
}
`,
    glsl: {
      vertex: /* glsl */ `#version 300 es
void main() {
  vec2 pos[3] = vec2[3](vec2(-1.0, -1.0), vec2(3.0, -1.0), vec2(-1.0, 3.0));
  gl_Position = vec4(pos[gl_VertexID], 0.0, 1.0);
}
`,
      fragment: /* glsl */ `#version 300 es
precision highp float;
uniform float u_dash;
uniform float u_docH;
uniform float u_docW;
uniform float u_phase;
uniform float u_rot;
uniform float u_scale;
uniform float u_tx;
uniform float u_ty;
uniform float u_viewH;
uniform float u_viewW;
uniform sampler2D u_mask;
out vec4 fragColor;

vec2 docAt(vec2 screen) {
  vec2 d = screen - vec2(u_tx, u_ty);
  float c = cos(u_rot);
  float s = sin(u_rot);
  return vec2(d.x * c + d.y * s, -d.x * s + d.y * c) / u_scale;
}

bool selectedAt(vec2 doc) {
  if (doc.x < 0.0 || doc.y < 0.0 || doc.x >= u_docW || doc.y >= u_docH) {
    return false;
  }
  // 上传按左上约定预翻转（backend.writeTexture），此处换算回数据行
  float v = texelFetch(u_mask, ivec2(int(doc.x), int(u_docH) - 1 - int(doc.y)), 0).r;
  return v >= 0.5;
}

void main() {
  // gl_FragCoord 原点左下 → 左上约定
  vec2 screen = vec2(gl_FragCoord.x, u_viewH - gl_FragCoord.y);
  bool center = selectedAt(docAt(screen));
  if (!center) {
    fragColor = vec4(0.0);
    return;
  }
  bool boundary =
    !selectedAt(docAt(screen + vec2(1.0, 0.0))) ||
    !selectedAt(docAt(screen - vec2(1.0, 0.0))) ||
    !selectedAt(docAt(screen + vec2(0.0, 1.0))) ||
    !selectedAt(docAt(screen - vec2(0.0, 1.0)));
  if (!boundary) {
    fragColor = vec4(0.0);
    return;
  }
  float t = floor((screen.x + screen.y + u_phase) / u_dash);
  fragColor = mod(t, 2.0) == 0.0 ? vec4(0.0, 0.0, 0.0, 1.0) : vec4(1.0, 1.0, 1.0, 1.0);
}
`,
    },
  };
}
